import { FaWhatsapp } from 'react-icons/fa';
import { ArrowRight } from 'lucide-react';
import { useSanityContext } from '@/sanity/SanityDataProvider';

// R2 - Product data structure for reusable card component
export interface Product {
  id: number;
  title: string;
  description: string;
  image: string;
  category: string;
  features: string[];
}

interface ProductCardProps {
  product: Product;
  index: number;
}

const WHATSAPP_FALLBACK_BASE = 'https://wa.me/?text=Hola%20INBOCA%2C%20me%20interesa%20';

/**
 * R2 - Reusable product card component with premium design
 * R4 - Consistent styling across FeaturedProductsSection and ProductosPage
 */
export function ProductCard({ product, index }: ProductCardProps) {
  const { data } = useSanityContext();
  const ci = data?.contactInfo;
  const whatsappBase = ci
    ? `https://wa.me/${ci.whatsappNumber}?text=Hola%20INBOCA%2C%20me%20interesa%20`
    : WHATSAPP_FALLBACK_BASE;

  return (
    <div
      className="group animate-fade-in-up"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Card Container - Clean design */}
      <div className="relative bg-white rounded-3xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 h-full flex flex-col">
        {/* Product Number Badge */}
        <div className="absolute top-4 left-4 z-10">
          <span className="flex items-center justify-center w-10 h-10 bg-inboca-teal text-white font-bold rounded-xl shadow-lg">
            {String(product.id).padStart(2, '0')}
          </span>
        </div>

        {/* Category Badge */}
        <div className="absolute top-4 right-4 z-10">
          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-semibold rounded-full shadow-sm">
            {product.category}
          </span>
        </div>

        {/* Image Container */}
        <div className="relative h-64 bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden">
          <img
            src={product.image}
            alt={product.title}
            className="w-full h-full object-contain object-center p-6 transition-transform duration-700 ease-out group-hover:scale-110"
          />
          {/* Subtle gradient at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent" />
        </div>

        {/* Content Section */}
        <div className="p-6 pt-4 flex flex-col flex-grow">
          {/* Title */}
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 group-hover:text-inboca-teal transition-colors duration-300">
            {product.title}
          </h3>

          {/* Description */}
          <p className="text-gray-600 text-sm mb-4 leading-relaxed flex-grow">
            {product.description}
          </p>

          {/* Features Tags */}
          <div className="flex flex-wrap gap-2 mb-5">
            {product.features.map((feature, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-inboca-gray text-gray-700 text-xs font-medium rounded-full"
              >
                {feature}
              </span>
            ))}
          </div>

          {/* CTA Button */}
          <a
            href={`${whatsappBase}${encodeURIComponent(product.title)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full bg-gray-900 text-white px-6 py-3.5 rounded-xl font-semibold transition-all duration-300 hover:bg-inboca-orange group/btn mt-auto"
          >
            <FaWhatsapp className="h-5 w-5" />
            <span>Solicitar Información</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
          </a>
        </div>
      </div>
    </div>
  );
}
